import { useTranslation } from 'react-i18next';

interface SupervisionDetailProps {
  onBookClick: () => void;
}

export default function SupervisionDetail({ onBookClick }: SupervisionDetailProps) {
  const { t } = useTranslation();

  const pillars = [
    { icon: 'ri-shield-check-line', title: 'Zero Conflict of Interest', desc: 'Your supervisor answers only to you — never the clinic.' },
    { icon: 'ri-file-list-3-line', title: 'Full Records Access', desc: 'Every scan, X-ray, and report is yours to keep.' },
    { icon: 'ri-user-heart-line', title: 'Continuity of Care', desc: 'Seamless handoff to your dentist back home.' },
    { icon: 'ri-time-line', title: 'Real-Time Oversight', desc: 'Supervision at every stage, not just the end.' },
  ];

  return (
    <section className="py-24 md:py-36 bg-[#111111]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-2 gap-12 md:gap-24 items-center">
          {/* Left image */}
          <div className="relative order-2 md:order-1">
            <div className="relative overflow-hidden" style={{ height: '520px' }}>
              <img
                src="https://public.readdy.ai/ai/img_res/c1ef0b0a358ef869cb64da0917be681a.jpg"
                alt="Clinic interior"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            </div>
            {/* Decorative gold frame */}
            <div className="absolute -top-4 -left-4 w-32 h-32 border border-amber-400/15" />
            <div className="absolute -bottom-4 -right-4 w-32 h-32 border border-amber-400/15" />
          </div>

          {/* Right content */}
          <div className="order-1 md:order-2">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-10 h-px bg-amber-400/50" />
              <span
                className="text-amber-400/80 text-xs font-medium tracking-[0.25em] uppercase"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Why It Matters
              </span>
            </div>
            <h2
              className="text-3xl md:text-5xl font-normal text-white leading-tight mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t('supervision.why.title')}
            </h2>
            <p
              className="text-white/40 text-base font-light leading-relaxed mb-12"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {t('supervision.why.desc')}
            </p>

            {/* Pillars */}
            <div className="grid grid-cols-2 gap-px bg-white/5 mb-12">
              {pillars.map((item, i) => (
                <div key={i} className="bg-[#111111] p-6 group hover:bg-[#161616] transition-colors duration-300">
                  <div className="w-8 h-8 flex items-center justify-center mb-4">
                    <i className={`${item.icon} text-xl text-amber-400/50 group-hover:text-amber-400 transition-colors duration-300`}></i>
                  </div>
                  <h4
                    className="text-sm font-medium text-white/70 mb-2"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {item.title}
                  </h4>
                  <p
                    className="text-xs text-white/30 font-light leading-relaxed"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>

            <button
              onClick={onBookClick}
              className="flex items-center gap-3 bg-amber-400 hover:bg-amber-300 text-black px-8 py-4 font-semibold text-sm tracking-widest uppercase transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {t('how.cta')}
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
